import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from "react-i18next";
import BaseTile from '../../../components/Tile/BaseTile';
import TileSettingsModal from '../../../components/common/TileSettingsModal';
import LocalFiltersActiveBadge from '../../../components/common/LocalFiltersActiveBadge';
import { useLocalFilters } from '../../../hooks/useLocalFilters';
import useAutomationPotentialData from './hooks/useAutomationPotentialData';
import AutomationPotentialKPISettings from './AutomationPotentialKPISettings';
import { Popover, PopoverTrigger, PopoverContent } from '../../../components/ui/popover';
import { useAutoFitFontSize } from '../_shared/useAutoFitFontSize';
import { formatCompactNumber } from '../../../utils/numberFormat';

const KPI_BLOCK_VALUE_MAX_PX = 36; // was text-4xl
const KPI_BLOCK_VALUE_MIN_PX = 16;

const INTERVAL_LABELS = { day: 'Day', month: 'Month', year: 'Year' };

const SettingsIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const KPIBlock = ({ label, value, unit, color, info }) => {
  const text = typeof value === 'number' ? value.toLocaleString('de-DE', { maximumFractionDigits: 2 }) : '—';
  const compactText = typeof value === 'number' ? formatCompactNumber(value) : undefined;
  const { ref, fontSizePx, displayText, isCompact } = useAutoFitFontSize({
    text,
    maxPx: KPI_BLOCK_VALUE_MAX_PX,
    minPx: KPI_BLOCK_VALUE_MIN_PX,
    compactText,
  });

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-1.5 py-0.5 rounded-xl border" style={{ borderColor: color + '55', backgroundColor: color + '12' }}>
      <div className="text-xs font-medium text-gray-500 flex items-center gap-1">
        {label}
        {info && (
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" aria-label="Show calculation"
                className="inline-flex text-gray-400 hover:text-gray-600 transition-colors focus:outline-none">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd" />
                </svg>
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" align="center" className="w-72 text-xs leading-relaxed p-3">
              {info}
            </PopoverContent>
          </Popover>
        )}
      </div>
      <div
        ref={ref}
        className="max-w-full truncate font-bold tabular-nums tracking-tight"
        style={{ color, fontSize: `${fontSizePx}px` }}
        title={isCompact ? text : undefined}
      >
        {displayText}
      </div>
      <div className="text-xs text-gray-500">{unit}</div>
    </div>
  );
};

const DEFAULT_STATE = {
  title: 'Automation Potential',
  objectType: '',
  activity: '',
  effortPerOccurrence: 10,
  manualDisplayInterval: 'day',
  monetaryEnabled: false,
  fullyLoadedHourlyRate: 45,
  monetaryDisplayInterval: 'year',
  color: '#6366f1',
  manualThresholdEnabled: false,
  manualThresholdValue: 0,
  manualColorBelow: '#22c55e',
  manualColorAbove: '#ef4444',
  monetaryThresholdEnabled: false,
  monetaryThresholdValue: 0,
  monetaryColorBelow: '#22c55e',
  monetaryColorAbove: '#ef4444',
};

const getFrequency = (countData, interval) => {
  if (!countData) return null;
  if (interval === 'day') return countData.per_day;
  if (interval === 'month') return countData.per_month;
  if (interval === 'year') return countData.per_year;
  return null;
};

const AutomationPotentialKPI = ({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onRegisterSettingsGetter,
  onTitleChange,
  initialSettings,
}) => {
  const { t } = useTranslation();
  const {
    localFilters,
    updateLocalFilter,
    clearLocalFilters,
    hasLocalFilters,
    localFilterCount,
    currentFilters,
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters,
    api,
  } = useLocalFilters(activeFile, initialSettings?.localFilters ?? null);

  const { objectTypes, objectTypeActivities, countData, loading, error, fetchCount } =
    useAutomationPotentialData(activeFile, api);

  const [title, setTitle] = useState(DEFAULT_STATE.title);
  const [objectType, setObjectType] = useState(DEFAULT_STATE.objectType);
  const [activity, setActivity] = useState(DEFAULT_STATE.activity);
  const [effortPerOccurrence, setEffortPerOccurrence] = useState(DEFAULT_STATE.effortPerOccurrence);
  const [manualDisplayInterval, setManualDisplayInterval] = useState(DEFAULT_STATE.manualDisplayInterval);
  const [monetaryEnabled, setMonetaryEnabled] = useState(DEFAULT_STATE.monetaryEnabled);
  const [fullyLoadedHourlyRate, setFullyLoadedHourlyRate] = useState(DEFAULT_STATE.fullyLoadedHourlyRate);
  const [monetaryDisplayInterval, setMonetaryDisplayInterval] = useState(DEFAULT_STATE.monetaryDisplayInterval);
  const [color, setColor] = useState(DEFAULT_STATE.color);
  const [manualThresholdEnabled, setManualThresholdEnabled] = useState(DEFAULT_STATE.manualThresholdEnabled);
  const [manualThresholdValue, setManualThresholdValue] = useState(DEFAULT_STATE.manualThresholdValue);
  const [manualColorBelow, setManualColorBelow] = useState(DEFAULT_STATE.manualColorBelow);
  const [manualColorAbove, setManualColorAbove] = useState(DEFAULT_STATE.manualColorAbove);
  const [monetaryThresholdEnabled, setMonetaryThresholdEnabled] = useState(DEFAULT_STATE.monetaryThresholdEnabled);
  const [monetaryThresholdValue, setMonetaryThresholdValue] = useState(DEFAULT_STATE.monetaryThresholdValue);
  const [monetaryColorBelow, setMonetaryColorBelow] = useState(DEFAULT_STATE.monetaryColorBelow);
  const [monetaryColorAbove, setMonetaryColorAbove] = useState(DEFAULT_STATE.monetaryColorAbove);

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    if (initialSettings.title !== undefined) { setTitle(initialSettings.title); onTitleChange?.(initialSettings.title); }
    if (initialSettings.objectType !== undefined) setObjectType(initialSettings.objectType);
    if (initialSettings.activity !== undefined) setActivity(initialSettings.activity);
    if (initialSettings.effortPerOccurrence !== undefined) setEffortPerOccurrence(initialSettings.effortPerOccurrence);
    if (initialSettings.manualDisplayInterval !== undefined) setManualDisplayInterval(initialSettings.manualDisplayInterval);
    if (initialSettings.monetaryEnabled !== undefined) setMonetaryEnabled(initialSettings.monetaryEnabled);
    if (initialSettings.fullyLoadedHourlyRate !== undefined) setFullyLoadedHourlyRate(initialSettings.fullyLoadedHourlyRate);
    if (initialSettings.monetaryDisplayInterval !== undefined) setMonetaryDisplayInterval(initialSettings.monetaryDisplayInterval);
    if (initialSettings.color !== undefined) setColor(initialSettings.color);
    if (initialSettings.manualThresholdEnabled !== undefined) setManualThresholdEnabled(initialSettings.manualThresholdEnabled);
    if (initialSettings.manualThresholdValue !== undefined) setManualThresholdValue(initialSettings.manualThresholdValue);
    if (initialSettings.manualColorBelow !== undefined) setManualColorBelow(initialSettings.manualColorBelow);
    if (initialSettings.manualColorAbove !== undefined) setManualColorAbove(initialSettings.manualColorAbove);
    if (initialSettings.monetaryThresholdEnabled !== undefined) setMonetaryThresholdEnabled(initialSettings.monetaryThresholdEnabled);
    if (initialSettings.monetaryThresholdValue !== undefined) setMonetaryThresholdValue(initialSettings.monetaryThresholdValue);
    if (initialSettings.monetaryColorBelow !== undefined) setMonetaryColorBelow(initialSettings.monetaryColorBelow);
    if (initialSettings.monetaryColorAbove !== undefined) setMonetaryColorAbove(initialSettings.monetaryColorAbove);
  }, []);

  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      title, objectType, activity, effortPerOccurrence, manualDisplayInterval,
      monetaryEnabled, fullyLoadedHourlyRate, monetaryDisplayInterval, color,
      manualThresholdEnabled, manualThresholdValue, manualColorBelow, manualColorAbove,
      monetaryThresholdEnabled, monetaryThresholdValue, monetaryColorBelow, monetaryColorAbove,
      localFilters,
    }));
  }, [title, objectType, activity, effortPerOccurrence, manualDisplayInterval,
      monetaryEnabled, fullyLoadedHourlyRate, monetaryDisplayInterval, color,
      manualThresholdEnabled, manualThresholdValue, manualColorBelow, manualColorAbove,
      monetaryThresholdEnabled, monetaryThresholdValue, monetaryColorBelow, monetaryColorAbove,
      localFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (objectType && activity) fetchCount(objectType, activity);
  }, [objectType, activity, currentFilters, activeFile]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenSettings = useCallback(() => {
    startDraftMode();
    setIsSettingsOpen(true);
  }, [startDraftMode]);

  const handleSettingsOpenChange = useCallback((val) => {
    setIsSettingsOpen(val);
    if (!val) cancelDraftFilters();
  }, [cancelDraftFilters]);

  const handleApply = useCallback((values) => {
    applyDraftFilters();
    setTitle(values.title);
    onTitleChange?.(values.title);
    setObjectType(values.objectType);
    setActivity(values.activity);
    setEffortPerOccurrence(values.effortPerOccurrence);
    setManualDisplayInterval(values.manualDisplayInterval);
    setMonetaryEnabled(values.monetaryEnabled);
    setFullyLoadedHourlyRate(values.fullyLoadedHourlyRate);
    setMonetaryDisplayInterval(values.monetaryDisplayInterval);
    setColor(values.color);
    setManualThresholdEnabled(values.manualThresholdEnabled);
    setManualThresholdValue(values.manualThresholdValue);
    setManualColorBelow(values.manualColorBelow);
    setManualColorAbove(values.manualColorAbove);
    setMonetaryThresholdEnabled(values.monetaryThresholdEnabled);
    setMonetaryThresholdValue(values.monetaryThresholdValue);
    setMonetaryColorBelow(values.monetaryColorBelow);
    setMonetaryColorAbove(values.monetaryColorAbove);
  }, [applyDraftFilters]);

  const manualFrequency = getFrequency(countData, manualDisplayInterval);
  const monetaryFrequency = getFrequency(countData, monetaryDisplayInterval);

  const manualPotential = useMemo(() => {
    if (manualFrequency == null || effortPerOccurrence == null) return null;
    return (manualFrequency * effortPerOccurrence) / 60;
  }, [manualFrequency, effortPerOccurrence]);

  // monetary = (events/interval × min/event ÷ 60) × €/h  →  €/interval
  const monetaryPotential = useMemo(() => {
    if (!monetaryEnabled || monetaryFrequency == null || effortPerOccurrence == null || !fullyLoadedHourlyRate) return null;
    return (monetaryFrequency * effortPerOccurrence / 60) * fullyLoadedHourlyRate;
  }, [monetaryEnabled, monetaryFrequency, effortPerOccurrence, fullyLoadedHourlyRate]);

  const manualDisplayColor = useMemo(() => {
    if (!manualThresholdEnabled || manualPotential == null) return color;
    return manualPotential < manualThresholdValue ? manualColorBelow : manualColorAbove;
  }, [manualThresholdEnabled, manualPotential, manualThresholdValue, color, manualColorBelow, manualColorAbove]);

  const monetaryDisplayColor = useMemo(() => {
    if (!monetaryThresholdEnabled || monetaryPotential == null) return color;
    return monetaryPotential < monetaryThresholdValue ? monetaryColorBelow : monetaryColorAbove;
  }, [monetaryThresholdEnabled, monetaryPotential, monetaryThresholdValue, color, monetaryColorBelow, monetaryColorAbove]);

  const manualIntervalLabel = INTERVAL_LABELS[manualDisplayInterval] || manualDisplayInterval;
  const monetaryIntervalLabel = INTERVAL_LABELS[monetaryDisplayInterval] || monetaryDisplayInterval;
  const isConfigured = Boolean(objectType && activity);

  const headerActions = useMemo(() => (
    <div className="flex items-center gap-1.5 min-w-0">
      {isConfigured && (
        <div className="relative group min-w-0">
          <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full border border-primary-200 block cursor-default overflow-hidden">
            <span className="block truncate">{activity}</span>
          </span>
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2.5 py-1 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900" />
            {activity}
          </div>
        </div>
      )}
      <div className="flex-shrink-0">
        <LocalFiltersActiveBadge
          hasLocalFilters={hasLocalFilters}
          localFilterCount={localFilterCount}
          onClearLocalFilters={clearLocalFilters}
        />
      </div>
      <button
        onClick={handleOpenSettings}
        className="flex-shrink-0 flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-label={t('common:actions.openSettings')}
      >
        <SettingsIcon />
        <span className="text-xs">{t('common:actions.settings')}</span>
      </button>
    </div>
  ), [isConfigured, activity, hasLocalFilters, localFilterCount, clearLocalFilters, handleOpenSettings]);

  useEffect(() => {
    if (onGetHeaderActions) onGetHeaderActions(headerActions);
  }, [onGetHeaderActions, headerActions]);

  return (
    <>
      <BaseTile
        title={title}
        loading={loading && isConfigured}
        error={error}
        placeholder={!isConfigured ? 'Please configure object type and activity in settings.' : null}
        padding="sm"
        minHeight="sm"
        contentClassName="-mt-3"
        className="p-3"
      >
        {isConfigured && !loading && !error && (
          <div className="flex flex-col gap-2 h-full">
            <KPIBlock
              label={`Manual Automation Potential / ${manualIntervalLabel}`}
              value={manualPotential}
              unit={`h/${manualIntervalLabel}`}
              color={manualDisplayColor}
              info={manualFrequency != null ? (
                <div className="space-y-1">
                  <p className="font-semibold text-gray-700 mb-1">How this is calculated</p>
                  <p className="text-gray-600">
                    {manualFrequency.toLocaleString('de-DE', { maximumFractionDigits: 2 })} occ./{manualIntervalLabel}
                    {' × '}{effortPerOccurrence} min ÷ 60
                  </p>
                  <p className="text-gray-800 font-semibold border-t border-gray-100 pt-1">
                    = {manualPotential?.toLocaleString('de-DE', { maximumFractionDigits: 2 })} h/{manualIntervalLabel}
                  </p>
                </div>
              ) : undefined}
            />
            {monetaryEnabled && (
              <KPIBlock
                label={`Monetary Potential / ${monetaryIntervalLabel}`}
                value={monetaryPotential}
                unit={`€/${monetaryIntervalLabel}`}
                color={monetaryDisplayColor}
                info={monetaryFrequency != null ? (
                  <div className="space-y-1">
                    <p className="font-semibold text-gray-700 mb-1">How this is calculated</p>
                    <p className="text-gray-600">
                      {monetaryFrequency.toLocaleString('de-DE', { maximumFractionDigits: 2 })} occ./{monetaryIntervalLabel}
                      {' × '}{effortPerOccurrence} min ÷ 60 × €{fullyLoadedHourlyRate}/h
                    </p>
                    <p className="text-gray-800 font-semibold border-t border-gray-100 pt-1">
                      = €{monetaryPotential?.toLocaleString('de-DE', { maximumFractionDigits: 2 })}/{monetaryIntervalLabel}
                    </p>
                  </div>
                ) : undefined}
              />
            )}
          </div>
        )}
      </BaseTile>

      <TileSettingsModal
        open={isSettingsOpen}
        onOpenChange={handleSettingsOpenChange}
        title="Automation Potential – Settings"
        getInitialValues={() => ({
          title, objectType, activity, effortPerOccurrence, manualDisplayInterval,
          monetaryEnabled, fullyLoadedHourlyRate, monetaryDisplayInterval, color,
          manualThresholdEnabled, manualThresholdValue, manualColorBelow, manualColorAbove,
          monetaryThresholdEnabled, monetaryThresholdValue, monetaryColorBelow, monetaryColorAbove,
        })}
        onApply={handleApply}
        applyButtonText="Apply"
      >
        {({ values, setField }) => (
          <AutomationPotentialKPISettings
            title={values.title}
            objectTypes={objectTypes}
            objectTypeActivities={objectTypeActivities}
            selectedObjectType={values.objectType}
            selectedActivity={values.activity}
            effortPerOccurrence={values.effortPerOccurrence}
            manualDisplayInterval={values.manualDisplayInterval}
            monetaryEnabled={values.monetaryEnabled}
            fullyLoadedHourlyRate={values.fullyLoadedHourlyRate}
            monetaryDisplayInterval={values.monetaryDisplayInterval}
            color={values.color}
            manualThresholdEnabled={values.manualThresholdEnabled}
            manualThresholdValue={values.manualThresholdValue}
            manualColorBelow={values.manualColorBelow}
            manualColorAbove={values.manualColorAbove}
            monetaryThresholdEnabled={values.monetaryThresholdEnabled}
            monetaryThresholdValue={values.monetaryThresholdValue}
            monetaryColorBelow={values.monetaryColorBelow}
            monetaryColorAbove={values.monetaryColorAbove}
            localFilters={localFilters}
            updateLocalFilter={updateLocalFilter}
            countData={countData}
            manualPotential={manualPotential}
            manualIntervalLabel={manualIntervalLabel}
            onTitleChange={(v) => setField('title', v)}
            onObjectTypeChange={(v) => { setField('objectType', v); setField('activity', ''); }}
            onActivityChange={(v) => setField('activity', v)}
            onEffortChange={(v) => setField('effortPerOccurrence', v)}
            onManualDisplayIntervalChange={(v) => setField('manualDisplayInterval', v)}
            onMonetaryEnabledChange={(v) => setField('monetaryEnabled', v)}
            onFullyLoadedHourlyRateChange={(v) => setField('fullyLoadedHourlyRate', v)}
            onMonetaryDisplayIntervalChange={(v) => setField('monetaryDisplayInterval', v)}
            onColorChange={(v) => setField('color', v)}
            onManualThresholdEnabledChange={(v) => setField('manualThresholdEnabled', v)}
            onManualThresholdValueChange={(v) => setField('manualThresholdValue', v)}
            onManualColorBelowChange={(v) => setField('manualColorBelow', v)}
            onManualColorAboveChange={(v) => setField('manualColorAbove', v)}
            onMonetaryThresholdEnabledChange={(v) => setField('monetaryThresholdEnabled', v)}
            onMonetaryThresholdValueChange={(v) => setField('monetaryThresholdValue', v)}
            onMonetaryColorBelowChange={(v) => setField('monetaryColorBelow', v)}
            onMonetaryColorAboveChange={(v) => setField('monetaryColorAbove', v)}
          />
        )}
      </TileSettingsModal>
    </>
  );
};

export default AutomationPotentialKPI;
